import type { Supplier } from "@albertine/types";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import type { Agent } from "../../common/types/Agent";
import type { Conversation } from "../../common/types/Conversation";
import type { Request } from "../../common/types/Request";
import Chat from "../Chat/Chat";
import MemberRequests from "../RequestListView/MemberRequests";
import RequestView from "../RequestView/RequestView";
import "./MobileMemberView.css";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import { useMemberRequests } from "../../common/hooks/useMemberRequests";
import { routeSchemas } from "../../utils/routes";
import EditBookingForm from "../Bookings/EditBookingForm";
import WithBookingAndRequest from "../CatalogBeta/WithBookingAndRequest";
import { MemberProfileHost } from "../MemberData/MemberProfileHost";
import { FetchRequestFromRoute } from "./FetchRequest";
import MobileViewFooter from "./MobileMemberViewFooter";
import MobileMemberViewHeader from "./MobileMemberViewHeader";

type Props = {
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
    conversation: Conversation;
    request?: Request;
    suppliers: Supplier[];
};

const MobileMemberView = function MobileMemberView({
    currentAgent,
    member,
    conversation,
    request,
    suppliers,
}: Props) {
    const { data: memberRequests } = useMemberRequests(member.id);
    const matchRequestBooking = useRouteMatch<{
        bookingId: string;
        requestId: string;
    }>(routeSchemas.requestBooking);

    if (!currentAgent) return null;

    return (
        <div className="column">
            <div className="tab">
                {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                <Switch>
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <Route path="/member">
                        {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                        <Route path="/member/:memberId/details" exact>
                            <MobileMemberViewHeader member={member} />
                            <MemberProfileHost
                                memberId={member.id}
                                currentAgent={currentAgent}
                            />
                        </Route>
                        {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                        <Route path="/member/:memberId/requests" exact>
                            <div className="tab mobile-view">
                                <MemberRequests
                                    requests={memberRequests}
                                    member={member}
                                />
                            </div>
                        </Route>
                        {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                        <Route
                            path={[
                                "/member/:memberId/",
                                "/member/:memberId/message/:messageId",
                            ]}
                            exact
                        >
                            <div className="mobile-view">
                                <MobileMemberViewHeader member={member} />
                                <Chat
                                    conversation={conversation}
                                    request={request}
                                    currentAgent={currentAgent}
                                    member={member}
                                />
                            </div>
                        </Route>
                    </Route>
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <Route path={routeSchemas.requestBooking}>
                        {matchRequestBooking && (
                            <WithBookingAndRequest
                                bookingId={matchRequestBooking.params.bookingId}
                                renderOnError={<div>Booking not found</div>}
                                renderLoading={<div>Loading...</div>}
                            >
                                {(booking, req) => (
                                    <EditBookingForm
                                        booking={booking}
                                        request={req}
                                        member={member}
                                        currentAgent={currentAgent}
                                        suppliers={suppliers}
                                    />
                                )}
                            </WithBookingAndRequest>
                        )}
                    </Route>
                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                    <Route path="/request/:requestId">
                        <FetchRequestFromRoute
                            path="/request/:requestId"
                            override={request}
                        >
                            {(fetchedRequest) => (
                                <>
                                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                                    <Route path="/request/:requestId/edit" exact>
                                        <RequestView
                                            request={fetchedRequest}
                                            currentAgent={currentAgent}
                                            memberTenant={member.tenant}
                                        />
                                    </Route>
                                    {/* @ts-ignore we are using outdated libraries that use ReactNode instead of JSX.Element. */}
                                    <Route
                                        path={[
                                            "/request/:requestId",
                                            "/request/:requestId/message/:messageId",
                                        ]}
                                        exact
                                    >
                                        <div className="mobile-view">
                                            <MobileMemberViewHeader member={member} />
                                            <Chat
                                                conversation={conversation}
                                                request={fetchedRequest}
                                                currentAgent={currentAgent}
                                                member={member}
                                            />
                                        </div>
                                    </Route>
                                </>
                            )}
                        </FetchRequestFromRoute>
                    </Route>
                </Switch>
            </div>
            <MobileViewFooter
                member={member}
                request={request}
                memberRequests={memberRequests || []}
            />
        </div>
    );
};

export default MobileMemberView;
